import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { toast } from '@/components/ui/toast';
import { fmtDate, fmtDateTime } from '@/lib/format-date';
import { LookupField } from '@/components/shared/lookup-field';
import { ASSET_REF_QUERY } from '@/lib/ref-defaults';

interface FieldDef {
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'asset-ref';
}

function FormPage({
  title, description, endpoint, fields, recentColumns, listKey,
}: {
  title: string;
  description: string;
  endpoint: string;
  fields: FieldDef[];
  recentColumns: Array<{ key: string; label: string; render?: (r: any) => React.ReactNode }>;
  listKey: string;
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = React.useState<Record<string, any>>({});

  const { data: recent = [] } = useQuery<any[]>({
    queryKey: ['recent', endpoint],
    queryFn: async () => (await api<any[]>(`${endpoint}?limit=10`)).data || [],
  });

  const create = useMutation({
    mutationFn: async () => {
      const body: Record<string, any> = {};
      for (const f of fields) {
        const v = draft[f.name];
        if (v === '' || v === undefined) continue;
        body[f.name] = f.type === 'number' ? Number(v) : v;
      }
      const r = await api(endpoint, { method: 'POST', body: JSON.stringify(body) });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast(`${title} saved`, undefined, 'success');
      setDraft({});
      qc.invalidateQueries({ queryKey: ['recent', endpoint] });
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">New entry</CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fields.map((f) => (
              <div key={f.name} className={f.type === 'asset-ref' ? 'md:col-span-2' : ''}>
                <Label>{f.label}</Label>
                {f.type === 'asset-ref' ? (
                  <LookupField
                    endpoint="/fleet/assets"
                    labelKey="fleetNumber"
                    value={(draft[f.name] as string) || null}
                    onChange={(id) => setDraft((d) => ({ ...d, [f.name]: id ?? '' }))}
                    filter={ASSET_REF_QUERY as any}
                    placeholder="— Pick an asset —"
                  />
                ) : (
                  <Input
                    type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                    value={(draft[f.name] as any) ?? ''}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.name]: e.target.value }))}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <Button onClick={() => create.mutate()} disabled={create.isPending}>
              <Save className="h-4 w-4 mr-1" /> Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent {title.toLowerCase()}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {recentColumns.map((c) => <TableHead key={c.key}>{c.label}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.length === 0 && (
                <TableRow><TableCell colSpan={recentColumns.length} className="text-center text-muted-foreground py-4">No recent {listKey}.</TableCell></TableRow>
              )}
              {recent.map((row: any) => (
                <TableRow key={row.id}>
                  {recentColumns.map((c) => (
                    <TableCell key={c.key} className="text-sm">
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export function WofRenewFormPage() {
  return (
    <FormPage
      title="Update WoF"
      description="Record a new Warrant of Fitness check for a vehicle."
      endpoint="/wof-renewals"
      listKey="WoF entries"
      fields={[
        { name: 'asset', label: 'Asset', type: 'asset-ref' },
        { name: 'wofCof', label: 'WoF / CoF', type: 'text' },
        { name: 'testingDate', label: 'Testing Date', type: 'date' },
        { name: 'expiryDate', label: 'Expiry Date', type: 'date' },
      ]}
      recentColumns={[
        { key: 'asset', label: 'Asset', render: (r) => r.asset?.fleetNumber || r.asset?.registrationNumber || '—' },
        { key: 'wofCof', label: 'WoF/CoF' },
        { key: 'testingDate', label: 'Tested', render: (r) => fmtDate(r.testingDate) },
        { key: 'expiryDate', label: 'Expires', render: (r) => fmtDate(r.expiryDate) },
      ]}
    />
  );
}

export function LicenceRenewFormPage() {
  return (
    <FormPage
      title="Renew a Licence"
      description="Record a new vehicle licence renewal (rego)."
      endpoint="/license-renewals"
      listKey="licence renewals"
      fields={[
        { name: 'asset', label: 'Asset', type: 'asset-ref' },
        { name: 'dateOfIssue', label: 'Date of Issue', type: 'date' },
        { name: 'licencePeriod', label: 'Licence Period (months)', type: 'number' },
        { name: 'nztaReference', label: 'NZTA Reference', type: 'text' },
        { name: 'purchaseAmount', label: 'Cost', type: 'number' },
      ]}
      recentColumns={[
        { key: 'asset', label: 'Asset', render: (r) => r.asset?.fleetNumber || r.asset?.registrationNumber || '—' },
        { key: 'dateOfIssue', label: 'Issued', render: (r) => fmtDate(r.dateOfIssue) },
        { key: 'licencePeriod', label: 'Months' },
        { key: 'purchaseAmount', label: 'Cost' },
      ]}
    />
  );
}
